import queryString from 'query-string';
import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosClient from 'api/axiosClient';
import { setGlobalLoading } from 'app/commonRedux/appSlice';
import { actions } from 'features/payroll/payrollSlice';
import { notificationToast } from 'components/notificationToast';
import { Notification } from 'constants/notification';
import payrollApi from 'api/payrollApi';
import i18n from 'i18next';

export const getPayRuns = createAsyncThunk('/payroll', async (params: any, thunkApi: any) => {
  try {
    const response = await axiosClient.get(`/payroll?${queryString.stringify(params)}`);
    return response;
  } catch (error) {
    return thunkApi.rejectWithValue(error);
  }
});

export const createPayRun = createAsyncThunk('/payroll', async (params: any, thunkApi: any) => {
  try {
    thunkApi.dispatch(setGlobalLoading({ loading: true }));
    const response = await axiosClient.post(`/payroll`, params?.body);
    response && params?.callBack();
    return response;
  } catch (error) {
    return thunkApi.rejectWithValue(error);
  } finally {
    thunkApi.dispatch(setGlobalLoading({ loading: false }));
  }
});

export const updatePayRun = createAsyncThunk('/payroll/:id', async (params: any, thunkApi: any) => {
  try {
    thunkApi.dispatch(setGlobalLoading({ loading: true }));
    const response = await axiosClient.put(`/payroll/${params?.id}`, params?.body);
    response && params?.callBack();
    return response;
  } catch (error) {
    return thunkApi.rejectWithValue(error);
  } finally {
    thunkApi.dispatch(setGlobalLoading({ loading: false }));
  }
});

export const deletePayRun = createAsyncThunk('/payroll/:id', async (params: any, thunkApi: any) => {
  try {
    thunkApi.dispatch(setGlobalLoading({ loading: true }));
    const response = await axiosClient.delete(`/payroll/${params?.id}`);
    response && params?.callBack();
    return response;
  } catch (error) {
    return thunkApi.rejectWithValue(error);
  } finally {
    thunkApi.dispatch(setGlobalLoading({ loading: false }));
  }
});

export const syncHiddenPayrolls = createAsyncThunk(
  '/payroll/sync-hidden',
  async (params: any, thunkApi: any) => {
    try {
      thunkApi.dispatch(setGlobalLoading({ loading: true }));
      const response = await axiosClient.post(
        `/payroll/sync-hidden?${queryString.stringify(params)}`,
      );
      if (params?.callBack) {
        params.callBack(response);
      }
      return response;
    } catch (error: any) {
      if (params?.callBack) {
        params.callBack({
          statusCode: error?.response?.status || 500,
          message:
            error?.response?.data?.message || error?.message || 'Failed to sync hidden payrolls',
          data: null,
        });
      }
      return thunkApi.rejectWithValue(error);
    } finally {
      thunkApi.dispatch(setGlobalLoading({ loading: false }));
    }
  },
);

export const cleanupOrphanPayrolls = createAsyncThunk(
  '/payroll/cleanup-orphan',
  async (params: any, thunkApi: any) => {
    try {
      thunkApi.dispatch(setGlobalLoading({ loading: true }));
      const response = await axiosClient.delete(
        `/payroll/cleanup-orphan?${queryString.stringify(params)}`,
      );
      if (params?.callBack) {
        params.callBack(response);
      }
      return response;
    } catch (error: any) {
      if (params?.callBack) {
        params.callBack({
          statusCode: error?.response?.status || 500,
          message:
            error?.response?.data?.message || error?.message || 'Failed to cleanup orphan payrolls',
          data: null,
        });
      }
      return thunkApi.rejectWithValue(error);
    } finally {
      thunkApi.dispatch(setGlobalLoading({ loading: false }));
    }
  },
);

export const getHistoryPayRuns = createAsyncThunk(
  '/payroll/history',
  async (params: any, thunkApi: any) => {
    try {
      const response = await axiosClient.get(`/payroll/history?${queryString.stringify(params)}`);
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const createPayroll = createAsyncThunk(
  '/payroll/details',
  async (params: any, thunkApi: any) => {
    const { setErrorMessage } = actions;
    try {
      thunkApi.dispatch(setGlobalLoading({ loading: true }));
      const response = await axiosClient.post(`/payroll/details`, params?.body);
      // Call callback with response (including error cases)
      if (params?.callBack) {
        params.callBack(response);
      }
      return response;
    } catch (error: any) {
      error?.fieldErrors && thunkApi.dispatch(setErrorMessage(error));
      // Call callback with error so frontend can handle it
      if (params?.callBack) {
        params.callBack({
          statusCode: error?.response?.status || 500,
          message: error?.response?.data?.message || error?.message || 'Failed to create pay run',
          data: null,
        });
      }
      return thunkApi.rejectWithValue(error);
    } finally {
      thunkApi.dispatch(setGlobalLoading({ loading: false }));
    }
  },
);

export const importPayrollCsv = createAsyncThunk(
  '/payroll/details/import-csv',
  async (params: any, thunkApi: any) => {
    const { setErrorMessage } = actions;
    try {
      thunkApi.dispatch(setGlobalLoading({ loading: true }));
      const response = await payrollApi.importPayrollCsv(params?.id, params?.file, params?.body);
      response && params?.callBack && params?.callBack(response);
      return response;
    } catch (error: any) {
      error?.fieldErrors && thunkApi.dispatch(setErrorMessage(error));
      return thunkApi.rejectWithValue(error);
    } finally {
      thunkApi.dispatch(setGlobalLoading({ loading: false }));
    }
  },
);

export const updatePayroll = createAsyncThunk(
  '/payroll/details/:id',
  async (params: any, thunkApi: any) => {
    const { setErrorMessage } = actions;
    try {
      thunkApi.dispatch(setGlobalLoading({ loading: true }));
      const response = await axiosClient.put(
        `/payroll/details/${params?.id}?actionType=${params?.actionType}`,
        params?.body,
      );
      response && params?.callBack();
      return response;
    } catch (error: any) {
      error?.fieldErrors && thunkApi.dispatch(setErrorMessage(error));
      return thunkApi.rejectWithValue(error);
    } finally {
      thunkApi.dispatch(setGlobalLoading({ loading: false }));
    }
  },
);

export const deletePayroll = createAsyncThunk(
  '/payroll/details/:id',
  async (params: any, thunkApi: any) => {
    try {
      thunkApi.dispatch(setGlobalLoading({ loading: true }));
      const response = await axiosClient.delete(`/payroll/details/${params?.id}`);
      response && params?.callBack();
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    } finally {
      thunkApi.dispatch(setGlobalLoading({ loading: false }));
    }
  },
);

export const getPayrollGroupUser = createAsyncThunk(
  '/payroll/details/:id',
  async (params: any, thunkApi: any) => {
    try {
      const response = await axiosClient.get(`/payroll/details?${queryString.stringify(params)}`);
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const getPayrollUser = createAsyncThunk(
  '/payroll/details/:payrollDetailsId/user/:userId',
  async (params: any, thunkApi: any) => {
    try {
      thunkApi.dispatch(setGlobalLoading({ loading: true }));
      const response = await axiosClient.get(
        `/payroll/details/${params?.payrollDetailsId}/user/${params?.userId}`,
      );
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    } finally {
      thunkApi.dispatch(setGlobalLoading({ loading: false }));
    }
  },
);

export const sendActionPayroll = createAsyncThunk(
  '/payroll/details/send-action-workflow',
  async (params: any, thunkApi: any) => {
    try {
      thunkApi.dispatch(setGlobalLoading({ loading: true }));
      const response = await axiosClient.post(
        `/payroll/details/send-action-workflow`,
        params?.body,
      );
      response && params?.callBack();
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    } finally {
      thunkApi.dispatch(setGlobalLoading({ loading: false }));
    }
  },
);

interface RemindResult {
  userId: string;
  fullName: string;
  ok: boolean;
  attachments: boolean;
  error?: string;
}

export const remindApprovePayroll = createAsyncThunk(
  '/payroll/details/:id/remind',
  async (
    params: { id: number | string; stepStatus?: string; callBack?: (data: any) => void },
    thunkApi: any,
  ) => {
    try {
      thunkApi.dispatch(setGlobalLoading({ loading: true }));
      const response: any = await axiosClient.post(`/payroll/details/${params?.id}/remind`, {
        stepStatus: params?.stepStatus,
      });
      // BE đã gửi (await) email nhắc duyệt trước khi trả response; toast phải phản ánh
      // đúng kết quả thật sự per-approver, không được báo "thành công" nếu có người thất bại.
      const results: RemindResult[] = response?.data?.results || [];
      const okResults = results.filter((r) => r.ok);
      const failedResults = results.filter((r) => !r.ok);
      const failedNames = failedResults.map((r) => r.fullName || r.userId).join(', ');

      params?.callBack?.(response?.data);

      if (results.length === 0 || okResults.length === 0) {
        notificationToast(
          Notification.Type.Error,
          i18n.t('payroll:remind_failed_all', { names: failedNames }),
          Notification.Duration._5s,
        );
        return thunkApi.rejectWithValue({
          message: i18n.t('payroll:remind_failed_all', { names: failedNames }),
        });
      }

      if (failedResults.length > 0) {
        notificationToast(
          Notification.Type.Warning,
          i18n.t('payroll:remind_sent_partial', {
            sent: okResults.length,
            total: results.length,
            names: failedNames,
          }),
          Notification.Duration._5s,
        );
      } else {
        const missingAttachments = okResults.some((r) => !r.attachments);
        const message = missingAttachments
          ? `${i18n.t('payroll:remind_sent_all', { count: okResults.length })} ${i18n.t(
              'payroll:remind_no_attachments',
            )}`
          : i18n.t('payroll:remind_sent_all', { count: okResults.length });
        notificationToast(Notification.Type.Success, message, Notification.Duration._3s);
      }

      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    } finally {
      thunkApi.dispatch(setGlobalLoading({ loading: false }));
    }
  },
);

export const exportPayrollOverviewByLine = createAsyncThunk(
  '/payroll/payroll-overview-line/export',
  async (params: { period?: string; locationId?: number }, thunkApi: any) => {
    try {
      thunkApi.dispatch(setGlobalLoading({ loading: true }));
      const response: any = await axiosClient.get(
        `/payroll/payroll-overview-line/export?${queryString.stringify(params)}`,
        { responseType: 'blob' },
      );
      const fileName = `PayrollOverviewByLine_${params?.period || ''}.xlsx`;
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    } finally {
      thunkApi.dispatch(setGlobalLoading({ loading: false }));
    }
  },
);

export const getPayrollUserStatuses = createAsyncThunk(
  '/payroll/details/:id/user-statuses',
  async (params: { id: number | string }, thunkApi: any) => {
    try {
      const response = await axiosClient.get(`/payroll/details/${params?.id}/user-statuses`);
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const getPayrollDetailHistory = createAsyncThunk(
  'payroll/details/:id/history',
  async (params: any, thunkApi: any) => {
    try {
      const response = await axiosClient.get(`payroll/details/${params?.id}/history`);
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const exportPayrollDetailExcel = createAsyncThunk(
  'payroll/details/:id/export/excel',
  async (params: { body: any; id: any }, thunkApi: any) => {
    const { body, id } = params;
    try {
      let str: any = `Payroll_Detail.xlsx`;
      thunkApi.dispatch(setGlobalLoading({ loading: true }));
      const response: any = await axiosClient.put(`payroll/details/${id}/export/excel`, body, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', str);
      document.body.appendChild(link);
      link.click();
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    } finally {
      thunkApi.dispatch(setGlobalLoading({ loading: false }));
    }
  },
);

export const exportPayrollDetailPdf = createAsyncThunk(
  'payroll/details/:id/export/pdf',
  async (params: { body: any; id: any }, thunkApi: any) => {
    const { body, id } = params;
    try {
      let str: any = `Payroll_Detail.zip`;
      thunkApi.dispatch(setGlobalLoading({ loading: true }));
      const response: any = await axiosClient.put(`payroll/details/${id}/export/pdf`, body, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', str);
      document.body.appendChild(link);
      link.click();
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    } finally {
      thunkApi.dispatch(setGlobalLoading({ loading: false }));
    }
  },
);

export const getPayslipByYear = createAsyncThunk(
  'payroll/payslip',
  async (params: any, thunkApi: any) => {
    try {
      const response = await axiosClient.get(`payroll/payslip?${queryString.stringify(params)}`);
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    }
  },
);

export const sendPayslipManual = createAsyncThunk(
  'payroll/send-payslip-manual',
  async (params: { body: any }, thunkApi: any) => {
    const { body } = params;
    try {
      thunkApi.dispatch(setGlobalLoading({ loading: true }));
      const response: any = await axiosClient.post(`payroll/send-payslip-manual`, body);
      const count = body?.userIds?.length || 0;
      notificationToast(
        Notification.Type.Success,
        i18n.t('payroll:payslip_sending_in_background', { count }),
        Notification.Duration._3s,
      );
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    } finally {
      thunkApi.dispatch(setGlobalLoading({ loading: false }));
    }
  },
);

export const getPaySlipDetail = createAsyncThunk(
  '/payroll/payslip-detail',
  async (params: any, thunkApi: any) => {
    try {
      thunkApi.dispatch(setGlobalLoading({ loading: true }));
      const response = await axiosClient.post(`/payroll/payslip-detail`, params?.body);
      response && params?.callBack();
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    } finally {
      thunkApi.dispatch(setGlobalLoading({ loading: false }));
    }
  },
);

export const syncPayroll = createAsyncThunk(
  '/payroll/sync-payroll',
  async (params: any, thunkApi: any) => {
    try {
      thunkApi.dispatch(setGlobalLoading({ loading: true }));
      const response = await axiosClient.post(`/payroll/sync-payroll`, params?.body);
      response && params?.callBack();
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    } finally {
      thunkApi.dispatch(setGlobalLoading({ loading: false }));
    }
  },
);

export const syncPayrollPrevMonthAllowance = createAsyncThunk(
  '/allowance-details/clone-allowance',
  async (params: any, thunkApi: any) => {
    try {
      const { callBack, ...other } = params;
      thunkApi.dispatch(setGlobalLoading({ loading: true }));
      const response = await axiosClient.post(
        `/allowance-details/clone-allowance?${queryString.stringify(other)}`,
      );
      response && callBack && callBack();
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    } finally {
      thunkApi.dispatch(setGlobalLoading({ loading: false }));
    }
  },
);

export const syncPayrollPrevMonthContactSalary = createAsyncThunk(
  '/contract-configure/clone-contract',
  async (params: any, thunkApi: any) => {
    try {
      const { callBack, ...other } = params;
      thunkApi.dispatch(setGlobalLoading({ loading: true }));
      const response = await axiosClient.post(
        `/contract-configure/clone-contract?${queryString.stringify(other)}`,
      );
      response && callBack && callBack();
      return response;
    } catch (error) {
      return thunkApi.rejectWithValue(error);
    } finally {
      thunkApi.dispatch(setGlobalLoading({ loading: false }));
    }
  },
);
